import { getSorensonEndpoint } from 'pages/JoinInterpreter/helpers';
import React, {
  FC,
  createContext,
  useState,
  useContext,
  useEffect,
} from 'react';
import { useAuth } from './auth-context';
import axios from 'axios';
import { UserType } from 'utils/constants';
import useZoom from 'utils/hooks/useZoom';
import { Meeting } from 'utils/types';
import { useNavigate } from 'react-router-dom';

interface IMeetingStatus {
  isInMeeting: boolean;
  isHost: boolean | null;
  isInterpreterConnected: boolean | null;
}

interface IInterpreterRequest {
  loading: boolean;
  error: boolean;
  inProcess: boolean;
  callId: string;
  errorMessage?: string;
}

interface IMeetingStatusContext extends IMeetingStatus {
  currentMeeting?: Meeting.IMeeting;
  interpreterRequest: IInterpreterRequest;
  initialLoad: boolean;
  setInterpreterRequest: (data: IInterpreterRequest) => void;
  getInterpreterStatus: (callId: string) => any;
  resetInterpreterRequest: () => void;
  isGettingMeetingInfo: boolean;
  setIsGettingMeetingInfo: (value: boolean) => void;
}

export const MeetingStatusContext = createContext<IMeetingStatusContext>({
  isInMeeting: false,
  isGettingMeetingInfo: false,
  setIsGettingMeetingInfo: () => {},
  isHost: null,
  isInterpreterConnected: null,
  currentMeeting: { joinUrl: '', userId: '', isHost: false },
  interpreterRequest: {
    loading: false,
    error: false,
    inProcess: false,
    callId: '',
  },
  initialLoad: true,
  setInterpreterRequest: (data) => console.log(data),
  getInterpreterStatus: () => '',
  resetInterpreterRequest: () => {},
});

export const MeetingStatusContextProvider: FC = ({ children }) => {
  const { sorensonUser, getTokenSilently, isLoggedIn } = useAuth();
  const { meeting, isInMeeting, initialLoad, roleError, error } = useZoom();
  const [isGettingMeetingInfo, setIsGettingMeetingInfo] =
    useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!meeting?.joinUrl && !initialLoad && isInMeeting && !isLoggedIn) {
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting, isInMeeting, initialLoad, isLoggedIn]);

  useEffect(() => {
    if (!initialLoad && !!meeting.joinUrl) {
      setIsGettingMeetingInfo(!isGettingMeetingInfo);
    }
    if (roleError || error) {
      setIsGettingMeetingInfo(false);
    }
  }, [meeting.joinUrl, roleError, error]);

  const [isInterpreterConnected, setIsInterpreterConnected] =
    useState<boolean>(false);

  const [interpreterRequest, setInterpreterRequest] =
    useState<IInterpreterRequest>({
      loading: false,
      error: false,
      inProcess: false,
      callId: '',
    });

  const resetInterpreterRequest = () => {
    setInterpreterRequest({
      loading: false,
      error: false,
      inProcess: false,
      callId: '',
      errorMessage: undefined,
    });
    setIsInterpreterConnected(false);
  };

  const getInterpreterStatus = async (callId: string) => {
    const url = getSorensonEndpoint(sorensonUser.userType);
    const apiKey =
      sorensonUser.userType === UserType.VRI
        ? process.env.REACT_APP_API_KEY_VRI
        : process.env.REACT_APP_API_KEY_VRS;
    try {
      const { data } = await axios.get(`${url}callstatus/${callId}`, {
        headers: {
          'x-api-key': apiKey as string,
          'Content-Type': 'application/json',
          Authorization: await getTokenSilently(),
        },
      });
      if (data === 'Connected') {
        setIsInterpreterConnected(true);
      }
      return data;
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <MeetingStatusContext.Provider
      value={{
        isInMeeting,
        isHost: !!meeting?.isHost,
        isInterpreterConnected,
        currentMeeting: meeting,
        interpreterRequest,
        initialLoad,
        setInterpreterRequest,
        getInterpreterStatus,
        resetInterpreterRequest,
        isGettingMeetingInfo,
        setIsGettingMeetingInfo,
      }}
    >
      {children}
    </MeetingStatusContext.Provider>
  );
};

export const useMeetingStatus = () => {
  return useContext(MeetingStatusContext);
};
